import React from 'react';

import type { LensCardType } from '~/types/leLens';

/**
 * A small drawing of what each card type looks like.
 *
 * Shared rather than owned by the card editor: the plan gate picks a type for
 * sixteen cards at once, and a reader choosing between "combo" and "stacked"
 * from words alone is guessing. One drawing, so the two surfaces cannot come to
 * depict the same chart differently.
 */
export default function ChartTypeGlyph({ type }: { type: LensCardType }) {
  const stroke = 'currentColor';
  const muted = 'currentColor';
  const base = {
    fill: 'none',
    stroke,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
  };

  if (type === 'metric') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M8 27.5H72" opacity="0.2" stroke={muted} strokeWidth="1" />
        <path d="M12 24H32M40 24H67" {...base} strokeWidth="4" />
        <path d="M40 14H61" opacity="0.35" stroke={muted} strokeWidth="3" />
      </svg>
    );
  }
  if (type === 'drivers') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M40 4V32" opacity="0.25" stroke={muted} strokeWidth="1" />
        <rect fill="currentColor" height="5" opacity="0.9" rx="1" width="25" x="40" y="7" />
        <rect fill="currentColor" height="5" opacity="0.72" rx="1" width="16" x="24" y="16" />
        <rect fill="currentColor" height="5" opacity="0.55" rx="1" width="10" x="40" y="25" />
      </svg>
    );
  }
  if (type === 'bar' || type === 'stacked') {
    const heights = [12, 20, 16];
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M8 29H72" opacity="0.25" stroke={muted} strokeWidth="1" />
        {[16, 34, 52].map((x, index) => {
          const total = heights[index];
          // Stacked divides each bar rather than adding to it: the two types
          // reach the same totals, and drawing the division inside the bar is
          // what tells them apart at the size a picker renders them.
          const lower = type === 'stacked' ? Math.round(total * 0.55) : total;
          return (
            <React.Fragment key={x}>
              <rect fill="currentColor" height={lower} opacity="0.88" rx="1" width="10" x={x} y={29 - lower} />
              {type === 'stacked' && (
                <rect
                  fill="currentColor"
                  height={total - lower - 1}
                  opacity="0.34"
                  rx="1"
                  width="10"
                  x={x}
                  y={29 - total}
                />
              )}
            </React.Fragment>
          );
        })}
      </svg>
    );
  }
  if (type === 'area') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M8 29H72" opacity="0.25" stroke={muted} strokeWidth="1" />
        <path d="M10 27L24 19L37 23L52 9L70 16V29H10Z" fill="currentColor" opacity="0.2" />
        <path d="M10 27L24 19L37 23L52 9L70 16" {...base} />
      </svg>
    );
  }
  if (type === 'scatter') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M8 29H72M10 31V6" opacity="0.25" stroke={muted} strokeWidth="1" />
        {[
          [20, 22],
          [31, 18],
          [39, 21],
          [53, 11],
          [64, 14],
        ].map(([cx, cy]) => (
          <circle cx={cx} cy={cy} fill="currentColor" key={`${cx}-${cy}`} r="3" />
        ))}
      </svg>
    );
  }
  if (type === 'pie') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M40 4A14 14 0 1 1 27 14H40Z" fill="currentColor" opacity="0.9" />
        <path d="M43 8A12 12 0 0 1 53 19H43Z" fill="#fff" opacity="0.75" />
        <path d="M28 20A12 12 0 0 0 40 32V20Z" fill="#fff" opacity="0.45" />
      </svg>
    );
  }
  if (type === 'heatmap') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        {[0, 1, 2]
          .flatMap((row) => [0, 1, 2, 3].map((column) => ({ row, column })))
          .map(({ row, column }) => (
            <rect
              fill="currentColor"
              height="7"
              key={`${row}-${column}`}
              opacity={0.2 + ((row * 4 + column) % 4) * 0.22}
              rx="1"
              width="11"
              x={17 + column * 12}
              y={7 + row * 8}
            />
          ))}
      </svg>
    );
  }
  if (type === 'boxplot') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <path d="M8 18H72M18 12V24M62 12V24" {...base} opacity="0.6" strokeWidth="1" />
        <rect fill="currentColor" height="14" opacity="0.25" rx="1" width="25" x="28" y="11" />
        <path d="M40.5 11V25" {...base} />
      </svg>
    );
  }
  if (type === 'table') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <rect fill="none" height="24" opacity="0.5" rx="2" stroke={muted} width="52" x="14" y="6" />
        <path d="M14 14H66M32 6V30M49 6V30M14 22H66" opacity="0.55" stroke={muted} strokeWidth="1" />
      </svg>
    );
  }
  if (type === 'insight') {
    return (
      <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
        <rect fill="currentColor" height="23" opacity="0.12" rx="4" width="52" x="14" y="6" />
        <path d="M22 13H55M22 18H49M22 23H42" {...base} opacity="0.8" strokeWidth="2" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" height="36" viewBox="0 0 80 36" width="80">
      <path d="M8 29H72" opacity="0.25" stroke={muted} strokeWidth="1" />
      <path d="M10 25L23 17L36 21L50 8L70 15" {...base} />
      {type === 'combo' && (
        <path d="M14 29V19H24V29M39 29V14H49V29M58 29V21H68V29" fill="currentColor" opacity="0.28" />
      )}
    </svg>
  );
}
